import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Table,
  Label,
  Loader,
  Button,
  Grid,
  Icon,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../../api';
import markdownComponents from '../markdownComponents';

const STATUS_COLORS = {
  draft: 'yellow',
  in_review: 'orange',
  effective: 'green',
  superseded: 'grey',
  obsolete: 'red',
};

const getDocCategory = (type) => {
  if (['POL', 'SOP', 'WI', 'FRM', 'TECH'].includes(type))
    return { label: 'Controlled Document', color: '#0070f3' };
  if (type === 'EXT') return { label: 'External Document', color: '#9333ea' };
  return { label: 'Record', color: '#6b7280' };
};

// Both must be module-level variables (not React state). ReactMarkdownComponent
// persists across component mounts; if remarkGfm were in useState it would reset
// to null on remount while mdReady (seeded from !!ReactMarkdownComponent) starts
// true, causing the loading effect to skip and leaving remarkGfm permanently null.
let ReactMarkdownComponent = null;
let remarkGfmPlugin = null;
let mdLoadPromise = null;

function loadMarkdownDeps() {
  if (mdLoadPromise) return mdLoadPromise;
  mdLoadPromise = Promise.all([
    import('react-markdown'),
    import('remark-gfm'),
  ]).then(([rm, rgfm]) => {
    ReactMarkdownComponent = rm.default || rm;
    remarkGfmPlugin = rgfm.default || rgfm;
  });
  return mdLoadPromise;
}

export default function DocumentDetail() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mdReady, setMdReady] = useState(!!ReactMarkdownComponent);

  useEffect(() => {
    fetchApi(`${getApiPath()}/@qms/register/${encodeURIComponent(id)}`)
      .then((json) => {
        setDoc(json);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    if (!doc || !doc.kb_slug) return;
    fetchApi(
      `${getApiPath()}/@docs/versions?slug=${encodeURIComponent(doc.kb_slug)}`,
    )
      .then((json) => setVersions(json.versions || []))
      .catch(() => setVersions([]));
  }, [doc]);

  useEffect(() => {
    if (!mdReady) {
      loadMarkdownDeps().then(() => {
        setMdReady(true);
      });
    }
  }, [mdReady]);

  if (loading) {
    return (
      <Container style={{ paddingTop: '3em' }}>
        <Loader active inline="centered" content="Loading document..." />
      </Container>
    );
  }
  if (error) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Segment color="red">Error: {error}</Segment>
      </Container>
    );
  }
  if (!doc) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Segment>Document not found.</Segment>
      </Container>
    );
  }

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '3em' }}>
      <Helmet title={`${doc.document_id} — ${doc.title}`} />
      <Link to="/qms/register">&larr; Document Register</Link>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginTop: '0.5em',
        }}
      >
        <Header as="h1" style={{ marginBottom: '0.5em' }}>
          {doc.document_id}
          <Header.Subheader>{doc.title}</Header.Subheader>
        </Header>
        <Button
          as={Link}
          to={`/qms/register/${encodeURIComponent(doc.document_id)}/edit`}
          primary
          size="small"
        >
          <Icon name="edit" /> Edit
        </Button>
      </div>

      {/* Metadata */}
      <Segment>
        <Grid columns={2} stackable>
          <Grid.Row>
            <Grid.Column>
              <Table definition compact size="small">
                <Table.Body>
                  <Table.Row>
                    <Table.Cell width={5}>Document ID</Table.Cell>
                    <Table.Cell><strong>{doc.document_id}</strong></Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Category</Table.Cell>
                    <Table.Cell>
                      {(() => {
                        const cat = getDocCategory(doc.document_type);
                        return (
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '0.2em 0.6em',
                              borderRadius: '3px',
                              fontSize: '0.85em',
                              fontWeight: 600,
                              color: '#fff',
                              backgroundColor: cat.color,
                            }}
                          >
                            {cat.label}
                          </span>
                        );
                      })()}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Type</Table.Cell>
                    <Table.Cell>{doc.document_type}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Domain</Table.Cell>
                    <Table.Cell>{doc.domain_code}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Version</Table.Cell>
                    <Table.Cell>{doc.version}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Status</Table.Cell>
                    <Table.Cell>
                      <Label color={STATUS_COLORS[doc.status] || 'grey'}>
                        {doc.status}
                      </Label>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Classification</Table.Cell>
                    <Table.Cell>{doc.classification}</Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </Grid.Column>
            <Grid.Column>
              <Table definition compact size="small">
                <Table.Body>
                  <Table.Row>
                    <Table.Cell width={5}>Owner</Table.Cell>
                    <Table.Cell>{doc.owner || '—'}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Author</Table.Cell>
                    <Table.Cell>{doc.author || '—'}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Reviewer</Table.Cell>
                    <Table.Cell>{doc.reviewer || '—'}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Approver</Table.Cell>
                    <Table.Cell>{doc.approver || '—'}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Effective Date</Table.Cell>
                    <Table.Cell>{doc.effective_date || '—'}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Next Review</Table.Cell>
                    <Table.Cell>{doc.next_review_date || '—'}</Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        {doc.location && (
          <p style={{ marginTop: '0.5em', color: '#888' }}>
            <Icon name="folder" /> {doc.location}
          </p>
        )}
        {doc.notes && (
          <p style={{ marginTop: '0.5em', color: '#555' }}>
            <strong>Notes:</strong> {doc.notes}
          </p>
        )}
      </Segment>

      {/* Body content — prefer KB content (kb_body_md) over inline (body_md) */}
      {(doc.kb_body_md || doc.body_md) ? (
        <>
          <Header as="h3">Content</Header>
          {doc.kb_slug && (
            <p>
              <Link to={`/docs/${doc.kb_slug}`}>
                View in Knowledge Base &rarr;
              </Link>
            </p>
          )}
          <Segment>
            {mdReady && ReactMarkdownComponent ? (
              <ReactMarkdownComponent
                remarkPlugins={remarkGfmPlugin ? [remarkGfmPlugin] : []}
                components={markdownComponents}
              >
                {doc.kb_body_md || doc.body_md}
              </ReactMarkdownComponent>
            ) : (
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: '0.9em',
                }}
              >
                {doc.kb_body_md || doc.body_md}
              </pre>
            )}
          </Segment>
        </>
      ) : (
        <Segment secondary>
          <p>
            No document content linked.
            {doc.location && (
              <> File path: <code>{doc.location}</code></>
            )}
          </p>
        </Segment>
      )}

      {/* Revision History — what versions exist and what changed (§4.2.4a) */}
      {versions.length > 0 && (
        <>
          <Header as="h3">Revision History</Header>
          <Table compact celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Version</Table.HeaderCell>
                <Table.HeaderCell>Date</Table.HeaderCell>
                <Table.HeaderCell>Author</Table.HeaderCell>
                <Table.HeaderCell>Changes</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {versions.map((v) => (
                <Table.Row key={v.id}>
                  <Table.Cell>
                    {doc.kb_slug ? (
                      <Link to={`/docs/${doc.kb_slug}/version?v=${encodeURIComponent(v.version)}`}>
                        <strong>v{v.version}</strong>
                      </Link>
                    ) : (
                      <strong>v{v.version}</strong>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {new Date(v.published_at).toLocaleDateString()}
                  </Table.Cell>
                  <Table.Cell>{v.author || '—'}</Table.Cell>
                  <Table.Cell>{v.changes || '—'}</Table.Cell>
                  <Table.Cell>
                    {v.superseded_at ? (
                      <Label size="tiny" color="grey">
                        Superseded &rarr; v{v.superseded_by}
                      </Label>
                    ) : (
                      <Label size="tiny" color="green">
                        Effective
                      </Label>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}

      {/* Approval Trail — proof the document went through the required process */}
      <Header as="h3">Approval Trail</Header>
      <Table compact celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Date</Table.HeaderCell>
            <Table.HeaderCell>Action</Table.HeaderCell>
            <Table.HeaderCell>From</Table.HeaderCell>
            <Table.HeaderCell>To</Table.HeaderCell>
            <Table.HeaderCell>By</Table.HeaderCell>
            <Table.HeaderCell>Comment</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {(doc.transitions || []).map((t) => (
            <Table.Row key={t.id}>
              <Table.Cell>
                {new Date(t.performed_at).toLocaleString()}
              </Table.Cell>
              <Table.Cell>
                <Label size="tiny">{t.action}</Label>
              </Table.Cell>
              <Table.Cell>
                {t.from_status
                  ? <><Label size="tiny" basic>{t.from_status}</Label>{' '}v{t.from_version}</>
                  : '—'}
              </Table.Cell>
              <Table.Cell>
                {t.to_status && (
                  <Label
                    size="tiny"
                    color={STATUS_COLORS[t.to_status] || 'grey'}
                  >
                    {t.to_status}
                  </Label>
                )}{' '}
                {t.to_version && `v${t.to_version}`}
              </Table.Cell>
              <Table.Cell>{t.performed_by}</Table.Cell>
              <Table.Cell>{t.comment || '—'}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      {/* Related Documents (traceability — IEC 62304 §5.7) */}
      {(doc.links || []).length > 0 && (() => {
        // Group links by relationship_label
        const grouped = {};
        (doc.links || []).forEach((lnk) => {
          const key = lnk.relationship_label || lnk.relationship_type || 'Related';
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(lnk);
        });
        return (
          <>
            <Header as="h3">
              <Icon name="linkify" /> Related Documents
            </Header>
            {Object.entries(grouped).map(([label, links]) => (
              <Segment key={label} style={{ marginBottom: '0.5em' }}>
                <Header as="h4" style={{ marginBottom: '0.5em' }}>
                  {label}
                  <Label size="tiny" circular style={{ marginLeft: '0.5em' }}>
                    {links.length}
                  </Label>
                </Header>
                <Table compact size="small">
                  <Table.Body>
                    {links.map((lnk, i) => (
                      <Table.Row key={`${lnk.related_doc_id}-${i}`}>
                        <Table.Cell width={1}>
                          <Label
                            size="mini"
                            basic
                            color={lnk.direction === 'outgoing' ? 'blue' : 'grey'}
                          >
                            {lnk.direction === 'outgoing' ? '\u2192' : '\u2190'}
                          </Label>
                        </Table.Cell>
                        <Table.Cell width={4}>
                          <Link
                            to={`/qms/register/${encodeURIComponent(lnk.related_doc_id)}`}
                          >
                            <strong>{lnk.related_doc_id}</strong>
                          </Link>
                        </Table.Cell>
                        <Table.Cell>
                          {lnk.related_title || ''}
                          {lnk.notes && (
                            <span style={{ color: '#888', marginLeft: '0.5em' }}>
                              — {lnk.notes}
                            </span>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </Segment>
            ))}
          </>
        );
      })()}

      {/* CR-2026-026: Evidence Records */}
      {(doc.evidence || []).length > 0 && (
        <>
          <Header as="h3">
            <Icon name="shield" /> Evidence Records
          </Header>
          <Table compact celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Date</Table.HeaderCell>
                <Table.HeaderCell>Phase</Table.HeaderCell>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Summary</Table.HeaderCell>
                <Table.HeaderCell>Recorded By</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {doc.evidence.map((ev) => (
                <Table.Row
                  key={ev.id}
                  style={ev.is_superseded ? { opacity: 0.5 } : undefined}
                >
                  <Table.Cell>
                    {new Date(ev.recorded_at).toLocaleString()}
                  </Table.Cell>
                  <Table.Cell>
                    <Label size="tiny" circular>
                      {ev.phase}
                    </Label>
                  </Table.Cell>
                  <Table.Cell>
                    {ev.evidence_type_label || ev.evidence_type}
                    {ev.is_superseded && (
                      <Label size="mini" color="grey" style={{ marginLeft: '0.5em' }}>
                        superseded
                      </Label>
                    )}
                  </Table.Cell>
                  <Table.Cell>{ev.evidence_text || '—'}</Table.Cell>
                  <Table.Cell>{ev.recorded_by}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}
    </Container>
  );
}
